import OpenFunnelModal from '@/components/dashboard/OpenFunnelModal.vue';
import { PROPOSAL_STAGES, PROPOSAL_WORKFLOW } from '@/constants/proposalTypes';
import { useUIStore } from '@/store/uiStore';
import { useUserStore } from '@/store/userStore';
import { getFormUrl } from '@/utils/urlHelper';

/**
 * Append query params to a form URL without duplicating `?` / `&`.
 */
function withQueryParams(baseUrl, params) {
  const hasAbsoluteBase = /^https?:\/\//i.test(baseUrl);
  const url = hasAbsoluteBase
    ? new URL(baseUrl)
    : new URL(baseUrl, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === '') return;
    url.searchParams.set(key, String(value));
  });
  // Preserve relative base URLs if the helper ever returns one
  if (!hasAbsoluteBase && typeof window !== 'undefined') {
    return `${url.pathname}${url.search}${url.hash}`;
  }
  return url.toString();
}

const STAGE_SEPARATOR = ':';

/**
 * Selection value is `mode` or `stage:mode` (e.g. `preliminary:extended`).
 */
function paramsForSelection(selection) {
  const [stage, mode] = selection.includes(STAGE_SEPARATOR)
    ? selection.split(STAGE_SEPARATOR)
    : [null, selection];
  const params = {};
  if (mode === 'extended') params.mode = 'extended';
  if (stage) params.proposal_stage = stage;
  return params;
}

/**
 * Prompt Standaard / Uitgebreid, then open the configurator for a new proposal.
 * On the measurement workflow the quote type comes first as tabs: voorlopig
 * (default, e.g. showroom; standard or extended) or definitief (measured;
 * extended only).
 */
export function useOpenProposalConfigurator() {
  const uiStore = useUIStore();
  const userStore = useUserStore();

  const buildNormalOption = (t, stage) => ({
    value: `${stage}${STAGE_SEPARATOR}normal`,
    icon: 'external-link',
    title: t('proposals.open_standard'),
    description: t('proposals.open_standard_description'),
  });

  const buildExtendedOption = (t, stage) => ({
    value: `${stage}${STAGE_SEPARATOR}extended`,
    icon: 'cubes',
    title: t('proposals.open_extended'),
    description: t('proposals.open_extended_description'),
  });

  const buildStages = (t) => {
    const isMeasurementWorkflow =
      userStore.currentOrganization?.settings?.proposal_workflow === PROPOSAL_WORKFLOW.MEASUREMENT;
    if (!isMeasurementWorkflow) return null;
    return [
      {
        value: PROPOSAL_STAGES.PRELIMINARY,
        title: t('proposals.open_stage_preliminary'),
        description: t('proposals.open_stage_preliminary_description'),
        options: [
          buildNormalOption(t, PROPOSAL_STAGES.PRELIMINARY),
          buildExtendedOption(t, PROPOSAL_STAGES.PRELIMINARY),
        ],
      },
      {
        value: PROPOSAL_STAGES.MEASURED,
        title: t('proposals.open_stage_definitive'),
        description: t('proposals.open_stage_definitive_description'),
        // Exact measurements are internal work, so there is no customer view to offer.
        options: [buildExtendedOption(t, PROPOSAL_STAGES.MEASURED)],
      },
    ];
  };

  /**
   * @param {Object} options
   * @param {string} options.formId
   * @param {string} [options.dealId]
   * @param {string} [options.leadId]
   * @param {string} [options.returnTo] - absolute URL to return to after submit
   * @param {Function} [$t] - i18n function; falls back to English-ish keys if omitted
   */
  function openProposalConfigurator({ formId, dealId = null, leadId = null, returnTo = null }, $t) {
    if (!formId) return;

    const t = typeof $t === 'function' ? $t : (key) => key;

    const modalId = uiStore.addModal(OpenFunnelModal, {
      title: t('proposals.open_configurator_title'),
      description: t('proposals.open_configurator_description'),
      size: 'sm',
      labels: {
        normalTitle: t('proposals.open_standard'),
        normalDescription: t('proposals.open_standard_description'),
        extendedTitle: t('proposals.open_extended'),
        extendedDescription: t('proposals.open_extended_description'),
      },
      stages: buildStages(t),
      defaultStage: PROPOSAL_STAGES.PRELIMINARY,
      onSelect: (selection) => {
        uiStore.closeModal(modalId);

        const baseUrl = getFormUrl(userStore.currentOrganization, formId);
        const params = paramsForSelection(selection);
        if (dealId) params.deal_id = dealId;
        if (leadId) params.lead_id = leadId;
        if (returnTo) params.return_to = returnTo;
        if (userStore.user?.id) params.opened_by = userStore.user.id;

        window.open(withQueryParams(baseUrl, params), '_blank');
      },
    });
  }

  return { openProposalConfigurator, withQueryParams };
}
